// © Microsoft Corporation. All rights reserved.

import { Glyph, GlyphType } from "@msnews/icon";

import { IntraArticleComponentType } from "./";
import { KeyValueArray } from "@msnews/core";

/**
 * Social media provider details interface
 */
interface SocialMediaProvider {
    apiUrl: string;
    attribution: string;
    name: string;
}

/**
 * Dictionary of social media provider configuration.
 */
const SocialMediaProviderConfig: KeyValueArray<IntraArticleComponentType, SocialMediaProvider> = [];
SocialMediaProviderConfig[IntraArticleComponentType.Unknown] = { apiUrl: "", attribution: "", name: "" };
SocialMediaProviderConfig[IntraArticleComponentType.FacebookEmbed] = { apiUrl: "//connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.5&appid=689384617806917", attribution: "Facebook Inc.", name: "Facebook" };
SocialMediaProviderConfig[IntraArticleComponentType.InstagramEmbed] = { apiUrl: "//instagram.com/embed.js", attribution: "Instagram Inc.", name: "Instagram" };
SocialMediaProviderConfig[IntraArticleComponentType.TwitterEmbed] = { apiUrl: "//platform.twitter.com/widgets.js", attribution: "Twitter Inc.", name: "Twitter" };

/**
 * Enumerates the available sharing option keys
 * @export
 */
enum SocialMedia {
    Email = "email",
    Facebook = "facebook",
    Line = "line",
    LinkedIn = "linkedin",
    Skype = "skype",
    Twitter = "twitter",
    VK = "vk",
    WhatsApp = "whatsapp"
}

/**
 * Possible target attribute to open social media linked pages
 */
enum SocialMediaTarget {
    _blank = "_blank",
    _self = "_self",
    _parent = "_parent",
    _top = "_top",
    newWindow = "newWindow"
}

/**
 * A map of social media items and its config attributes
 * @export
 * @type {[key in SocialMedia]: SocialMediaItemConfigAttributes}
 */
type SocialMediaConfig = {[key in SocialMedia]: SocialMediaItemConfigAttributes};

/**
 * The default configuration values for social media. Those can be overriden per experience
 * @export
 * @type {SocialMediaConfig}
 */
const SocialMediaDefaultConfig: SocialMediaConfig = {
    [SocialMedia.Email]: {
        ariaLabel: "share via email",
        backgroundColor: "#0078D7",
        color: "white",
        icon: Glyph.Mail,
        label: "Mail",
        target: SocialMediaTarget._self,
        url: "mailto:?subject={title}&body={body}"
    },
    [SocialMedia.Facebook]: {
        ariaLabel: "share on facebook",
        backgroundColor: "#4267B2",
        color: "white",
        icon: Glyph.Facebook,
        label: "Facebook",
        target: SocialMediaTarget._blank,
        url: "https://www.facebook.com/sharer.php?u={url}&amp;t={title}"
    },
    [SocialMedia.Line]: {
        ariaLabel: "share on line",
        backgroundColor: "#00C300",
        color: "white",
        icon: Glyph.Line,
        label: "Line",
        target: SocialMediaTarget._blank,
        url: "https://social-plugins.line.me/lineit/share?url={url}"
    },
    [SocialMedia.LinkedIn]: {
        ariaLabel: "share on linkedin",
        backgroundColor: "#0083BE",
        color: "white",
        icon: Glyph.LinkedIn,
        label: "LinkedIn",
        target: SocialMediaTarget._blank,
        url: "https://www.linkedin.com/shareArticle?mini=true&url={url}&title={title}&source={refererUrl}"
    },
    [SocialMedia.Skype]: {
        ariaLabel: "share on skype",
        backgroundColor: "#33BFFC",
        color: "white",
        icon: Glyph.Skype,
        label: "Skype",
        target: SocialMediaTarget._blank,
        url: "https://web.skype.com/share?url={url}&flow_id={flowid}&source=msn"
    },
    [SocialMedia.Twitter]: {
        ariaLabel: "share on twitter",
        backgroundColor: "#20A2F2",
        color: "white",
        icon: Glyph.Twitter,
        label: "Twitter",
        target: SocialMediaTarget._blank,
        url: "https://twitter.com/share?url={url}&text={title}&original_referer={refererUrl}"
    },
    [SocialMedia.WhatsApp]: {
        ariaLabel: "share on whatsapp",
        backgroundColor: "#25D366",
        color: "white",
        icon: Glyph.WhatsApp,
        label: "WhatsApp",
        target: SocialMediaTarget._blank,
        url: "https://web.whatsapp.com/send?text={url}"
    },
    [SocialMedia.VK]: {
        ariaLabel: "share on vk.com",
        backgroundColor: "#4680C2",
        color: "white",
        icon: Glyph.Vkcom,
        label: "VK",
        target: SocialMediaTarget._blank,
        url: "http://vk.com/share.php?url={url}"
    }
};

/**
 * The config attributed type for a given social media
 * @export
 */
type SocialMediaItemConfigAttributes = {
    /** Arial labe text */
    ariaLabel?: string,
    /** Attributes to be spread in the anchor tag element */
    attributes?: React.HTMLAttributes<HTMLAnchorElement>,
    /** Background color of the icon div */
    backgroundColor?: string,
    /** Foreground color of the icon div */
    color?: string,
    /** Fill colour for  when using svg icons */
    fill?: string,
    /** Icon glyph to override default value */
    icon?: GlyphType,
    /** Label text to override default value */
    label?: string,
    /** To be used in js window.open features param if target = newWindow */
    newWindowOptions?: any,
    /** telemetry click event should be handled by the caller using this callback */
    newWindowTelemetryCall?: (event: any) => void,
    /** ocid to be appended in the shared link */
    ocid?: string,
    /** target to be placed in anchor tag. newWindow has a customized js method that can make use of newWindowOptions attribute */
    target?: SocialMediaTarget,
    /** Share url to be hard overriden */
    url?: string
};

export {
    SocialMedia,
    SocialMediaProvider,
    SocialMediaProviderConfig,
    SocialMediaConfig,
    SocialMediaDefaultConfig,
    SocialMediaItemConfigAttributes,
    SocialMediaTarget
};
